// Story 5.5 — TanStack Query mutation hook for POST
// /api/v1/marketplace/listings/share/{listing_id}/buy.
//
// The EPIC-5 KEYSTONE buy hook. Mirrors useCreateShareListing (Story 5.2)
// in shape but the body is empty `{}` and the listing_id rides in the
// URL path:
//   * Generates an Idempotency-Key per call (Story 1.6 contract; the
//     middleware caches the success envelope so a network-retry replay
//     returns the cached 200 instead of a 409 LISTING_NOT_ACTIVE).
//   * Branched error envelope per `code` so BuyListingConfirmModal can
//     render distinct UX states:
//       - INSUFFICIENT_BALANCE (422) → Top up CTA opening <TopUpModal>;
//         on top-up success the modal mints a fresh idempotency key + retries.
//       - LISTING_NOT_ACTIVE (409) → "sold/cancelled/expired" error state.
//       - CANNOT_BUY_OWN_LISTING (409) → self-buy guard error.
//       - LISTING_NOT_FOUND (404) → stale listing surface.
//       - UNAUTHORIZED (401) → stale session.
//       - IDEMPOTENCY_CONFLICT (409) → key reused with different body.
//
// NO onSuccess cache invalidation in the hook itself — the consumer
// (BuyListingConfirmModal / MarketplacePage) owns invalidation because
// it knows which queries to refresh (vault + balance + marketplace browse).

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export interface BuyShareListingVariables {
  listing_id: string;
  /** The price the buyer saw. Echoed as `?expected_price_cents=` so the
   *  server rejects (409 LISTING_PRICE_CHANGED) if the seller edited it
   *  since the modal opened, rather than charging the new amount. */
  expected_price_cents?: number;
}

export interface BuyShareListingFeeBreakdown {
  platform_fee_cents: number;
  creator_royalty_cents: number;
  estimated_proceeds_cents: number;
}

export interface BuyShareListingResponseData {
  listing_id: string;
  share_id: string;
  price_cents: number;
  fee_breakdown: BuyShareListingFeeBreakdown;
  balance_after_cents: number;
  vault_redirect_url: string;
}

export interface BuyShareListingError {
  code: string;
  message: string;
  field?: string;
  /** Present when code === "INSUFFICIENT_BALANCE" (Story 1.15 envelope). */
  required_cents?: number;
  available_cents?: number;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postBuyShareListing(
  variables: BuyShareListingVariables,
): Promise<BuyShareListingResponseData> {
  const qs =
    variables.expected_price_cents != null
      ? `?expected_price_cents=${variables.expected_price_cents}`
      : "";
  const response = await fetch(
    `/api/v1/marketplace/listings/share/${variables.listing_id}/buy${qs}`,
    {
      method: "POST",
      credentials: "include",
      headers: {
        "content-type": "application/json",
        "x-idempotency-key": freshIdempotencyKey(),
      },
      body: "{}",
    },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: BuyShareListingError = {
      code: "BUY_LISTING_FAILED",
      message: `Purchase failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          // INSUFFICIENT_BALANCE envelope nests cents under `details`.
          required_cents: raw.error.details?.required_cents,
          available_cents: raw.error.details?.available_cents,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as { data: BuyShareListingResponseData };
  return body.data;
}

export function useBuyShareListing(): UseMutationResult<
  BuyShareListingResponseData,
  BuyShareListingError,
  BuyShareListingVariables
> {
  return useMutation<
    BuyShareListingResponseData,
    BuyShareListingError,
    BuyShareListingVariables
  >({
    mutationFn: postBuyShareListing,
    // NO onSuccess — caller invalidates vault-items + wallet-balance +
    // marketplace-share-listings via queryClient.
  });
}
